import React, {useEffect, useState} from 'react';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../../shared/interfaces';
import {ISavedSearchTreeViewResponse} from '../../../../../../api/types/savedSearch.type';
import {useSavedSearchTreeView} from '../../../../../../hooks/saved-search/savedSearch';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {Box} from '@mui/material';
import './SharedSavedSearchTree.scss';
import {getExpandedIdsForNode} from '../../../../../saved-search/SavedSearchUtils';
import {setSelectedTreeItemId} from '../../../../../../redux/slice/savedSearchSlice';

interface ISavedSearchTree {
  getSelectedTreeItem: (item: string) => void;
  getSelectedTreeItemData?: (
    item: TreeViewBaseItem<ExtendedTreeItemProps> | undefined
  ) => void;
  searchKeyword?: string;
}

const SharedSavedSearchTree: React.FC<ISavedSearchTree> = ({
  getSelectedTreeItem,
  getSelectedTreeItemData,
  searchKeyword,
}) => {
  const dispatch = useDispatch();

  const storedSelectedItem = useSelector(
    (state: RootState) => state.savedSearch.selectedTreeItemId
  );
  const createCustomIndex = useSelector(
    (state: RootState) => state?.indexManagement?.createCustomIndex
  );

  const isReadOnlyView = useSelector(
    (state: RootState) => state?.indexManagement?.isReadOnlyView
  );
  const {data: treeData, refetch} = useSavedSearchTreeView();
  const [selectedItem, setSelectedItem] = useState<string>(
    createCustomIndex?.documentSelection?.selectedSavedSearchId ?? ''
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [treeKey, setTreeKey] = useState<number>(0);
  const [selectedTreeItem, setSelectedTreeItem] = useState<
    ISavedSearchTreeViewResponse | undefined
  >(undefined);

  const transformToTreeItems = (
    flatList: ISavedSearchTreeViewResponse[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    for (const node of flatList) {
      nodeMap.set(node.id, {
        label: node.textWithoutCount || node.text,
        showMoreOptions: true,
        children: [],
        isDefault: false,
        tooltipTitle: node.toolTipRef_Title,
        tooltipContent: node.toolTipRef_Content,
        ...node,
        isGroup: false,
        id: node.id,
        disabled: isReadOnlyView,
      });
    }

    // Second pass: build tree and set flags
    for (const node of flatList) {
      const current = nodeMap.get(node.id);
      if (!current) continue;

      if (node.parent && node.parent !== '#' && nodeMap.has(node.parent)) {
        const parent = nodeMap.get(node.parent)!;
        (parent.children ??= []).push(current);
        parent.isGroup = true;
        parent.skipParent = true;
      } else if (!node.parent || node.parent === '#') {
        // Treat "#" or undefined/null as root
        tree.push(current);
      }
      // Else: skip orphaned children whose parent is missing
    }

    return tree;
  };

  const transformedTree = transformToTreeItems(treeData);

  useEffect(() => {
    if (!selectedItem) return;
    if (!treeData || !Array.isArray(treeData)) return;

    // If Redux has stored value
    if (storedSelectedItem) {
      const storedNode = treeData.find(
        (node) => node.id === storedSelectedItem
      );

      if (storedNode) {
        setSelectedItem(storedNode.id);
        setSelectedTreeItem(storedNode);
        getSelectedTreeItem(storedNode.id);
        getSelectedTreeItemData?.(
          storedNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
        return;
      }
    }

    // fallback to first root node
    const firstNode = treeData.find(
      (node) => node.parent === '#' || node.parent === '-1'
    );

    if (firstNode) {
      setSelectedItem(firstNode.id);
      setSelectedTreeItem(firstNode);
      getSelectedTreeItem(firstNode.id);
      getSelectedTreeItemData?.(
        firstNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
      );
    }
  }, [treeData, selectedItem, searchKeyword]);

  useEffect(() => {
    if (storedSelectedItem) {
      const expanded = getExpandedIdsForNode(
        transformedTree,
        storedSelectedItem
      );
      setExpandedIds(expanded);
      setTreeKey((prev) => prev + 1);
    }
  }, [storedSelectedItem, JSON.stringify(transformedTree)]);

  const handleSelected = (item: string[] | null) => {
    if (Array.isArray(item) && item.length > 0 && treeData) {
      setSelectedItem(item[0]);
      dispatch(setSelectedTreeItemId(item[0]));
      getSelectedTreeItem(item[0]);
      const foundItem = treeData.find((data: any) => data.id == item[0]);
      setSelectedTreeItem(foundItem);
      getSelectedTreeItemData &&
        getSelectedTreeItemData?.(
          foundItem as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
    } else {
      setSelectedItem('');
      dispatch(setSelectedTreeItemId(null));
      getSelectedTreeItem('');
      setSelectedTreeItem(undefined);
    }
  };

  return (
    <Box
      className="shared-search-tree-container"
      data-testid="shared-saearch-tree-container"
    >
      <ArrTreeView
        key={treeKey}
        setSelectedItem={handleSelected}
        showTooltip
        itemsList={transformedTree}
        selectedItem={selectedItem}
        handleNoResultMessage={'Loading...'}
        allowGlobalSelect={false}
        treeSelectedItems={[selectedItem]}
        multipleSelection={false}
        isShowCountLabel={true}
        showActions={false}
        expandSelect={false}
        searchKeyword={searchKeyword}
        defaultExpandedIds={expandedIds}
        isShowCheckbox={true}
        checkboxGroupSelection={false}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default SharedSavedSearchTree;
